// @flow
import React from 'react';
import { groupBy } from 'lodash';
import LedgersAccountsTab from './LedgerAccountsTab';
import type { LedgerAccount } from '../types';
import { GET_LEDGER_CATEGORY_ORDER } from '../queries';
import { NullState } from '../../../bench-internal-theme';
import { WrappedSpinner } from '../../../spinner';
import { useQuery } from '@apollo/client';
import { useClient } from '../../ClientContext';
import useLedgerAccounts from '../useLedgerAccounts';
import useDisplayGroups from '../useDisplayGroups';

type LedgersAndAccountsContainerProps = {
  search: string,
  showAccountsOnly: boolean,
  showActiveOnly: boolean,
  isSorting: boolean,
  clearFiltersForSorting: () => void,
};

const LedgerAccountsTabContainer = ({
  search,
  showAccountsOnly,
  showActiveOnly,
  isSorting,
  clearFiltersForSorting,
}: LedgersAndAccountsContainerProps) => {
  const searchLedgerAccounts = (ledgerAccounts: Array<LedgerAccount>): Array<LedgerAccount> => {
    if (search) {
      return ledgerAccounts.filter(ledgerAccount =>
        ledgerAccount.name.toLowerCase().includes(search.toLowerCase()),
      );
    }

    return ledgerAccounts;
  };

  const filterLedgerAccounts = (ledgerAccounts: Array<LedgerAccount>): Array<LedgerAccount> => {
    if (showAccountsOnly && showActiveOnly) {
      return ledgerAccounts.filter(ledgerAccount => ledgerAccount.role && ledgerAccount.active);
    }

    if (showAccountsOnly && !showActiveOnly) {
      return ledgerAccounts.filter(ledgerAccount => ledgerAccount.role);
    }

    if (!showAccountsOnly && showActiveOnly) {
      return ledgerAccounts.filter(ledgerAccount => ledgerAccount.active);
    }
    return ledgerAccounts;
  };

  const client = useClient();

  // Prefetch display groups to populate cache for sidebar (eliminates lag on edit)
  useDisplayGroups();

  const {
    ledgerAccounts: useLedgerAccountsResponse,
    isLoading: isUseLedgerAccountsLoading,
    hasError: hasUseLedgerAccountError,
  } = useLedgerAccounts();

  const { loading, error, data } = useQuery(GET_LEDGER_CATEGORY_ORDER, {
    variables: { clientId: client?.id },
  });

  if (error || hasUseLedgerAccountError) {
    return (
      <NullState
        bigEmoji="🙈"
        description={error?.message || hasUseLedgerAccountError?.message}
        title="Uh oh! The following error was encountered while fetching the data for this page."
      />
    );
  }
  if (loading || isUseLedgerAccountsLoading) {
    return <WrappedSpinner />;
  }
  const ledgerAccounts = searchLedgerAccounts(
    filterLedgerAccounts(useLedgerAccountsResponse || []),
  );

  return (
    <LedgersAccountsTab
      search={search}
      ledgerCategoryOrders={data?.ledgerCategoryOrders}
      isSorting={isSorting}
      canSort={useLedgerAccountsResponse.length > 1}
      clearFiltersForSorting={clearFiltersForSorting}
      ledgerAccountsByCategory={groupBy(ledgerAccounts, 'category.id')}
    />
  );
};

export default LedgerAccountsTabContainer;
